import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { ReferenceData } from '@/features/transactions/types';

type Category = ReferenceData['categories'][number];

/**
 * Seletor de categoria com criação no lugar.
 *
 * ================================================================
 * POR QUE CRIAR AQUI DENTRO
 * ================================================================
 *
 * A categoria certa quase sempre só é notada NA HORA de lançar — é ali que se
 * percebe que "Streaming" ainda não existe. Mandar o usuário até o Plano de
 * contas custa o formulário inteiro que ele já preencheu, e o preço disso não é
 * digitação perdida: é ele escolher "Sem categoria" para não perder o trabalho.
 *
 * Categoria em branco não dá erro nenhum. Ela só reaparece meses depois, como
 * uma fatia "Sem classificação" no relatório — a maior de todas, e a única que
 * não explica nada.
 *
 * ================================================================
 * O QUE ESTE ATALHO NÃO FAZ
 * ================================================================
 *
 * Cria categoria RAIZ e compartilhada, com o comportamento padrão. Pai,
 * essencial, cor e código continuam no Plano de contas, que é a tela desenhada
 * para pensar a árvore. Um formulário de lançamento não é lugar de decidir
 * hierarquia — e oferecer tudo aqui produziria uma árvore montada às pressas,
 * que é pior do que uma categoria a mais na raiz.
 */
export function CategorySelect({
  value,
  onChange,
  type,
  categories,
  emptyLabel = 'Sem categoria',
}: {
  value: string;
  onChange: (value: string) => void;
  /** Governa o filtro E o tipo da categoria criada aqui. */
  type: 'receita' | 'despesa';
  categories: Category[];
  emptyLabel?: string;
}) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: () =>
      api.post<{ data: { id: number } }>('/categories', {
        name: name.trim(),
        type,
        // Sem `entity_id`: nulo é compartilhada entre entidades (RF-CAT-02).
        // Uma categoria exclusiva criada de passagem some do seletor assim que
        // o usuário troca de entidade, e ele não faz ideia do porquê.
      }),
    onSuccess: (res) => {
      // `reference` alimenta os seletores de TODAS as telas; sem invalidar, a
      // categoria recém-criada não apareceria nem no próprio campo que a criou.
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
      void queryClient.invalidateQueries({ queryKey: ['categories'] });

      onChange(String(res.data.id));
      setName('');
      setCreating(false);
    },
  });

  const erro = create.error instanceof ApiError ? create.error : null;

  return (
    <>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input w-full">
        <option value="">{emptyLabel}</option>
        {categories.map((c) => (
          <option key={c.id} value={c.id}>{c.full_path}</option>
        ))}
      </select>

      {creating ? (
        <div className="mt-1.5 flex gap-1.5">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              // Enter aqui NÃO pode submeter o formulário que envolve este
              // componente: o usuário perderia o lançamento inteiro tentando
              // salvar uma categoria.
              if (e.key === 'Enter') {
                e.preventDefault();
                if (name.trim().length >= 2) create.mutate();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setCreating(false);
              }
            }}
            autoFocus
            maxLength={180}
            placeholder={type === 'receita' ? 'Ex.: Aluguéis recebidos' : 'Ex.: Streaming'}
            className="input w-full text-sm"
          />
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={name.trim().length < 2 || create.isPending}
            className="whitespace-nowrap rounded-lg bg-indigo-600 px-3 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {create.isPending ? '…' : 'Criar'}
          </button>
          <button
            type="button"
            onClick={() => { setCreating(false); setName(''); }}
            className="whitespace-nowrap px-1 text-sm text-slate-500 dark:text-slate-400 hover:underline"
          >
            cancelar
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mt-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
        >
          + criar categoria de {type}
        </button>
      )}

      {erro && (
        <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
          {erro.fieldError('name') ?? erro.message}
        </p>
      )}

      {/*
        Lista vazia precisa se explicar.

        Um select com uma opção só não parece uma base sem plano de contas —
        parece tela quebrada. Em produção a base pode nascer sem categoria
        nenhuma, e o silêncio deixa o usuário sem saber que há algo a fazer.
      */}
      {categories.length === 0 && !creating && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-500">
          Nenhuma categoria de {type} ainda. Crie uma aqui, ou instale o plano de contas padrão
          inteiro no menu <strong>Plano de contas</strong>.
        </p>
      )}
    </>
  );
}
